import React, { useContext, useEffect, useRef, useState } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import { Search, Eye, Trash2, Layers, RefreshCw, X } from "lucide-react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/gcstaff/staffflashcards/FlashCardsPage.css";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ── Format descryption text (bold markdown) ── */
const formatDesc = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <p key={i} className="gcteacher-fc-modal__back-line">
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        )}
      </p>
    );
  });
};

/* ── Format date ── */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

/* ══════════════════════════════════════
   Flash Card Modal
══════════════════════════════════════ */
const FlashCardModal = ({ card, show, onClose }) => {
  const [flipped, setFlipped] = useState(false);

  React.useEffect(() => {
    if (show) setFlipped(false);
  }, [show, card?.id]);

  if (!card) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="gcteacher-fc-modal-wrapper"
      backdropClassName="gcteacher-fc-backdrop"
    >
      <div className="gcteacher-fc-modal">

        {/* Header */}
        <div className="gcteacher-fc-modal__header">
          <div>
            <h5 className="gcteacher-fc-modal__title">{card.title}</h5>
            {card.subtitle && (
              <p className="gcteacher-fc-modal__source">{card.subtitle}</p>
            )}
          </div>
          <button className="gcteacher-fc-modal__close" onClick={onClose}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Card flip area */}
        <div className="gcteacher-fc-modal__scene">
          <div className={`gcteacher-fc-modal__card ${flipped ? "gcteacher-fc-modal__card--flipped" : ""}`}>

            {/* Front face — title */}
            <div className="gcteacher-fc-modal__face gcteacher-fc-modal__face--front">
              <Layers size={36} strokeWidth={1.4} className="gcteacher-fc-modal__face-icon" />
              <h6 className="gcteacher-fc-modal__face-title">{card.title}</h6>
              <p className="gcteacher-fc-modal__face-hint">Click to reveal content</p>
            </div>

            {/* Back face — descryption */}
            <div className="gcteacher-fc-modal__face gcteacher-fc-modal__face--back">
              <div className="gcteacher-fc-modal__back-content">
                {formatDesc(card.descryption)}
              </div>
            </div>
          </div>
        </div>

        {/* Flip button */}
        <div className="gcteacher-fc-modal__footer">
          <button
            className="gcteacher-fc-modal__flip-btn"
            onClick={() => setFlipped((prev) => !prev)}
          >
            <RefreshCw
              size={15}
              strokeWidth={2.2}
              className={flipped ? "gcteacher-fc-spin" : ""}
            />
            Flip Card
          </button>
        </div>

      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════
   Flash Cards Page (Teacher)
══════════════════════════════════════ */
const ITEMS_PER_PAGE = 9; // 3x3 grid per page (lg pe 3 columns k hisaab se)

const FlashCardsPage = () => {
  const { baseUrl, authToken, loggedInUser } = useContext(AppContext);

  const [searchQuery, setSearchQuery]   = useState("");
  const [cards, setCards]               = useState([]);
  const [loading, setLoading]           = useState(true);  // initial API fetch loading
  const [viewLoading, setViewLoading]   = useState(false); // page-switch / search loading (sirf visible cards k liye)
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [currentPage, setCurrentPage]   = useState(1); // pagination state

  // Pehli render par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  /* ── Fetch flash cards ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setLoading(false); return; }

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/flashcards/fetchAllFlashCards`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setCards(res.data.flashCards ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch flash cards:", err))
      .finally(() => setLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Delete flash card ── */
  const handleDelete = (id, e) => {
    e.stopPropagation();

    const formData = new FormData();
    formData.append("id", id);
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/flashcards/deleteFlashCard`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setCards((prev) => prev.filter((c) => c.id !== id));
        }
      })
      .catch((err) => console.error("Failed to delete flash card:", err));
  };

  const handleView  = (card) => { setSelectedCard(card); setShowModal(true); };
  const handleClose = () => { setShowModal(false); setTimeout(() => setSelectedCard(null), 300); };

  const filtered = cards.filter(
    (c) =>
      c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ── Pagination slice (sirf current page k cards grid mein jayenge) ── */
  const paginatedCards = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Search change hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /* ── Sirf visible cards ki loading dikhayein:
     - Jab search query change ho
     - Jab pagination se dusre page par jayen
     Poori list ek sath "pop" nahi hogi, sirf jitne cards is waqt
     screen par show hone hain unki hi loading chalegi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery]);

  return (
    <div className="gcteacher-flashcards">

      {/* ── Header ── */}
      <div className="gcteacher-flashcards__header">
        <h2>My Flash Cards</h2>
        <p>Quick study cards created from your content and PDFs using AI.</p>
      </div>

      {/* ── Search ── */}
      <div className="gcteacher-flashcards__search-row">
        <div className="gcteacher-flashcards__search-wrap">
          <input
            type="text"
            className="gcteacher-flashcards__search-input"
            placeholder="Search flash cards..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="gcteacher-flashcards__search-btn">
          <Search size={15} strokeWidth={2.2} />
        </button>
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Flash cards loading, Please wait..." />}

      {/* ── Cards Grid ── */}
      {!loading && (
        <>
          {viewLoading ? (
            /* ── Page/search switch loading — sirf grid ki, baaki page affect nahi hota ── */
            <Loader message="Loading flash cards..." />
          ) : (
            <Row className="g-3 mt-1">
              {paginatedCards.map((card, i) => (
                <Col xs={12} sm={6} lg={4} key={card.id}>
                  <div
                    className="gcteacher-fc-card"
                    style={{ "--card-index": i }}
                    onClick={() => handleView(card)}
                  >
                    {/* Top */}
                    <div className="gcteacher-fc-card__top">
                      <div className="gcteacher-fc-card__icon-wrap">
                        <Layers size={20} strokeWidth={1.6} />
                      </div>
                      <div className="gcteacher-fc-card__actions">
                        <button
                          className="gcteacher-fc-card__action-btn gcteacher-fc-card__action-btn--view"
                          onClick={(e) => { e.stopPropagation(); handleView(card); }}
                          title="View"
                        >
                          <Eye size={15} strokeWidth={2} />
                        </button>
                        <button
                          className="gcteacher-fc-card__action-btn gcteacher-fc-card__action-btn--delete"
                          onClick={(e) => handleDelete(card.id, e)}
                          title="Delete"
                        >
                          <Trash2 size={15} strokeWidth={2} />
                        </button>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="gcteacher-fc-card__info">
                      <h6 className="gcteacher-fc-card__title">{card.title}</h6>
                      {card.subtitle && (
                        <p className="gcteacher-fc-card__source">{card.subtitle}</p>
                      )}
                    </div>

                    {/* Bottom: type + date */}
                    <div className="gcteacher-fc-card__bottom">
                      <span className={`gcteacher-fc-tag gcteacher-fc-tag--${card.type?.toLowerCase()}`}>
                        {card.type}
                      </span>
                      <span className="gcteacher-fc-card__date">{formatDate(card.created_at)}</span>
                    </div>
                  </div>
                </Col>
              ))}

              {filtered.length === 0 && (
                <Col xs={12}>
                  <div className="gcteacher-flashcards__empty">
                    <Layers size={38} strokeWidth={1.2} />
                    <p>No flash cards found.</p>
                  </div>
                </Col>
              )}
            </Row>
          )}

          {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* ── Modal ── */}
      <FlashCardModal card={selectedCard} show={showModal} onClose={handleClose} />
    </div>
  );
};

export default FlashCardsPage;